/**
 * The hub shell refreshes its layout once when it has no account (a client
 * navigation from /hub/login leaves the layout stale; see HubShell). A ref
 * kept that to once per mount, but a refresh whose RSC request fails (a 5xx,
 * a timeout, a dropped connection) makes Next fall back to a whole page load
 * of the same URL. That load mounts a new shell with a fresh ref, which, with
 * a cookie whose session has gone, has no account again and refreshes again:
 * a reload loop for as long as the refresh keeps failing.
 *
 * So the claim is remembered for the tab (sessionStorage survives the reload)
 * and a second one inside the window is refused. Once an account is there the
 * mark is cleared, so the next sign in still gets its refresh.
 */

export const STALE_REFRESH_KEY = 'fv-hub-stale-layout-refresh';
export const STALE_REFRESH_WINDOW_MS = 60_000;

type Store = Pick<Storage, 'getItem' | 'setItem'>;

/**
 * True when this shell may refresh now; records the claim when it may. `key`
 * lets the dead-session trip (session-ended.ts) keep its own once-a-minute mark.
 */
export function claimStaleLayoutRefresh(store: Store | null | undefined, now: number, key = STALE_REFRESH_KEY): boolean {
  if (!store) return true;
  try {
    const last = Number(store.getItem(key));
    // A clock that moved backwards (last in the future) does not block.
    if (last > 0 && now >= last && now - last < STALE_REFRESH_WINDOW_MS) return false;
    store.setItem(key, String(now));
    return true;
  } catch {
    // Storage blocked: keep the old once-per-mount behaviour.
    return true;
  }
}

/** The layout has an account: let the next stale layout refresh straight away. */
export function clearStaleLayoutRefresh(store: Pick<Storage, 'removeItem'> | null | undefined, key = STALE_REFRESH_KEY): void {
  try {
    store?.removeItem(key);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** sessionStorage, or null where reading it throws (blocked site data). */
export function tabStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
